// hoasClient.ts — typed client for HOA management endpoints (§8.6 / D2).
//
// Wraps:
//   GET/POST/PATCH/DELETE /api/hoas/*
//   GET/POST              /api/hoas/:hoaId/ccrs
//   GET                   /api/hoas/:hoaId/ccrs/:versionId
//   GET/PUT               /api/properties/:propertyId/hoa
//   GET                   /api/properties
//
// JSON requests use the shared `api` helper (auth header + 401 refresh-retry).
// File uploads use FormData + fetch directly (the api helper is JSON-only).

import { api, ApiError } from './api';
import { getAccessToken, refreshAccessToken } from './authService';
import type { HoaRecord, CcrVersionRecord } from '@/types/db';

const API_BASE =
  import.meta.env.VITE_API_URL ?? 'https://api.applications.welcomehps.com';

// Re-export for page use
export type { HoaRecord, CcrVersionRecord };

// ---------------------------------------------------------------------------
// Property shape returned by GET /api/properties
// ---------------------------------------------------------------------------

export interface PropertySummary {
  propertyId: string;
  name: string;
  address: string | null;
  propertyType: string | null;
}

// ---------------------------------------------------------------------------
// HOA list/detail shapes
// ---------------------------------------------------------------------------

export interface HoaWithCount extends HoaRecord {
  propertyCount: number;
}

export interface AssociatedProperty {
  propertyId: string;
  name: string;
  address: string | null;
}

export interface HoaDetail {
  hoa: HoaRecord;
  properties: AssociatedProperty[];
  ccrs: CcrVersionRecord[];
}

export interface CcrDetail {
  ccr: CcrVersionRecord;
  originalUrl: string;
  optimizedUrl: string;
}

// ---------------------------------------------------------------------------
// HOA CRUD
// ---------------------------------------------------------------------------

export async function listHoas(): Promise<HoaWithCount[]> {
  const r = await api.get<{ hoas: HoaWithCount[] }>('/api/hoas');
  return r.hoas;
}

export async function createHoa(name: string): Promise<HoaRecord> {
  const r = await api.post<{ hoa: HoaRecord }>('/api/hoas', { name });
  return r.hoa;
}

export async function getHoaDetail(hoaId: string): Promise<HoaDetail> {
  return api.get<HoaDetail>(`/api/hoas/${hoaId}`);
}

export async function updateHoaName(hoaId: string, name: string): Promise<HoaRecord> {
  // api helper only exposes get/post — use fetch directly for PATCH.
  const token = await getAccessToken();
  let res = await fetch(`${API_BASE}/api/hoas/${hoaId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ name }),
  });
  if (res.status === 401 && (await refreshAccessToken())) {
    const newToken = await getAccessToken();
    res = await fetch(`${API_BASE}/api/hoas/${hoaId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
      },
      body: JSON.stringify({ name }),
    });
  }
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* empty */ }
    throw new ApiError(res.status, body);
  }
  const data = await res.json() as { hoa: HoaRecord };
  return data.hoa;
}

/**
 * Update the HOA's default monthly fee (cents). Send `null` to clear.
 * Per-lease overrides on LeaseFormState.hoaFeeOverrideCents win over this.
 */
export async function updateHoaDefaultFee(
  hoaId: string,
  defaultFeeCents: number | null,
): Promise<HoaRecord> {
  const token = await getAccessToken();
  let res = await fetch(`${API_BASE}/api/hoas/${hoaId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify({ defaultFeeCents }),
  });
  if (res.status === 401 && (await refreshAccessToken())) {
    const newToken = await getAccessToken();
    res = await fetch(`${API_BASE}/api/hoas/${hoaId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
      },
      body: JSON.stringify({ defaultFeeCents }),
    });
  }
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* empty */ }
    throw new ApiError(res.status, body);
  }
  const data = await res.json() as { hoa: HoaRecord };
  return data.hoa;
}

export async function deleteHoa(hoaId: string): Promise<void> {
  const token = await getAccessToken();
  let res = await fetch(`${API_BASE}/api/hoas/${hoaId}`, {
    method: 'DELETE',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401 && (await refreshAccessToken())) {
    const newToken = await getAccessToken();
    res = await fetch(`${API_BASE}/api/hoas/${hoaId}`, {
      method: 'DELETE',
      headers: newToken ? { Authorization: `Bearer ${newToken}` } : {},
    });
  }
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* empty */ }
    throw new ApiError(res.status, body);
  }
}

/**
 * CONFIRM an owner-created (needs_review) HOA — clears the staff TODO. Idempotent.
 * Uses the `api` helper (POST with an empty body).
 */
export async function confirmHoa(hoaId: string): Promise<HoaRecord> {
  const r = await api.post<{ hoa: HoaRecord }>(`/api/hoas/${hoaId}/confirm`, {});
  return r.hoa;
}

export interface MergeHoaResult {
  ok: boolean;
  target: HoaRecord;
  movedCcrs: number;
  movedProperties: number;
  repointedIntakes: number;
}

/**
 * MERGE the source HOA (hoaId) into the target — moves its CC&R versions + property
 * links (and any deferred owner-intake associations) into the target, then deletes
 * the source. For folding an owner-created duplicate into the real HOA. Idempotent.
 */
export async function mergeHoaInto(
  hoaId: string,
  targetHoaId: string,
): Promise<MergeHoaResult> {
  return api.post<MergeHoaResult>(`/api/hoas/${hoaId}/merge-into`, { targetHoaId });
}

// ---------------------------------------------------------------------------
// CC&R versions
// ---------------------------------------------------------------------------

export async function getCcrDetail(hoaId: string, versionId: string): Promise<CcrDetail> {
  return api.get<CcrDetail>(`/api/hoas/${hoaId}/ccrs/${versionId}`);
}

/**
 * Upload a CC&R PDF for an HOA. Uses FormData because this is a multipart upload.
 * Returns the created CcrVersionRecord.
 */
export async function uploadHoaCcr(hoaId: string, file: File): Promise<CcrVersionRecord> {
  const formData = new FormData();
  formData.append('file', file);

  const token = await getAccessToken();
  let res = await fetch(`${API_BASE}/api/hoas/${hoaId}/ccrs`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: formData,
  });
  if (res.status === 401 && (await refreshAccessToken())) {
    const newToken = await getAccessToken();
    res = await fetch(`${API_BASE}/api/hoas/${hoaId}/ccrs`, {
      method: 'POST',
      headers: newToken ? { Authorization: `Bearer ${newToken}` } : {},
      body: formData,
    });
  }
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* empty */ }
    throw new ApiError(res.status, body);
  }
  const data = await res.json() as { ccr: CcrVersionRecord };
  return data.ccr;
}

// ---------------------------------------------------------------------------
// Property ↔ HOA association
// ---------------------------------------------------------------------------

export async function getPropertyHoa(propertyId: string): Promise<{ hoaId: string | null }> {
  return api.get<{ hoaId: string | null }>(`/api/properties/${propertyId}/hoa`);
}

/**
 * Update a property's HOA association.
 *
 * `expectedHoaId` (Phase E Codex S3): if provided, the server runs an optimistic-
 * concurrency check — it reads the property's CURRENT hoaId and 409s if it
 * doesn't match `expectedHoaId`. Use this when the UI shows the user a stale
 * view and they decide to act on it; the server protects against overwriting
 * a newer association.
 *   - expectedHoaId = string → "I think the property is on this HOA"
 *   - expectedHoaId = null   → "I think the property has no HOA"
 *   - expectedHoaId omitted  → skip the check (legacy / first-time association)
 */
export async function setPropertyHoa(
  propertyId: string,
  hoaId: string | null,
  expectedHoaId?: string | null,
): Promise<{ hoaId: string | null }> {
  const body: { hoaId: string | null; expectedHoaId?: string | null } = { hoaId };
  if (expectedHoaId !== undefined) body.expectedHoaId = expectedHoaId;
  const token = await getAccessToken();
  let res = await fetch(`${API_BASE}/api/properties/${propertyId}/hoa`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (res.status === 401 && (await refreshAccessToken())) {
    const newToken = await getAccessToken();
    res = await fetch(`${API_BASE}/api/properties/${propertyId}/hoa`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(newToken ? { Authorization: `Bearer ${newToken}` } : {}),
      },
      body: JSON.stringify(body),
    });
  }
  if (!res.ok) {
    let errBody: unknown = null;
    try { errBody = await res.json(); } catch { /* empty */ }
    throw new ApiError(res.status, errBody);
  }
  return (await res.json()) as { hoaId: string | null };
}

// ---------------------------------------------------------------------------
// Property list (spec gap #5)
// ---------------------------------------------------------------------------

export async function listProperties(): Promise<PropertySummary[]> {
  const r = await api.get<{ properties: PropertySummary[] }>('/api/properties');
  return r.properties;
}

// ---------------------------------------------------------------------------
// Per-property settings — the staff-settable `hasYard` override
// ---------------------------------------------------------------------------

/**
 * Per-property settings response. `hasYard`:
 *   true  → property has a resident-responsible yard (yard-care applies);
 *   false → no resident yard (yard-care does not apply);
 *   null  → unset → yard-care falls back to the AppFolio proxy.
 * `readEnabled` reflects PROPERTY_SETTINGS_READ — when false the saved value is
 * stored but not yet live (staff can pre-populate ahead of activation).
 */
export interface PropertySettings {
  hasYard: boolean | null;
  readEnabled: boolean;
}

export async function getPropertySettings(
  propertyId: string,
): Promise<PropertySettings> {
  return api.get<PropertySettings>(`/api/properties/${propertyId}/settings`);
}

/** Set (or clear with null) a property's `hasYard` override. */
export async function setPropertyHasYard(
  propertyId: string,
  hasYard: boolean | null,
): Promise<PropertySettings> {
  const token = await getAccessToken();
  const doPut = (t: string | null) =>
    fetch(`${API_BASE}/api/properties/${propertyId}/settings`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        ...(t ? { Authorization: `Bearer ${t}` } : {}),
      },
      body: JSON.stringify({ hasYard }),
    });
  let res = await doPut(token);
  if (res.status === 401 && (await refreshAccessToken())) {
    res = await doPut(await getAccessToken());
  }
  if (!res.ok) {
    let body: unknown = null;
    try { body = await res.json(); } catch { /* empty */ }
    throw new ApiError(res.status, body);
  }
  return (await res.json()) as PropertySettings;
}

// Meeting 2026-05-14: one-off CC&R upload/delete from the lease builder was
// removed. CC&Rs are managed exclusively on the HOA detail page; the lease
// builder only overrides default monthly fees per-lease.
